import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatOption, MatSelect } from '@angular/material/select';
import { EntityPickerComponent } from 'entity-picker-lib';
import { RUNTIME_ENVIRONMENT } from 'environments-lib';
import { EntityType, IPickerInfo, IRuntimeEnvironment, ISharedServiceProviderService, RenderMode } from 'interfaces-lib';
import { IRecipientInfo, IRecipientInfoService } from 'recipient-info-service-lib';
import { SharedServiceProviderServiceFactory } from 'shared-service-provider-lib';
import { IdentityStorage } from 'storage-service-lib';
import { TerminologySentenceBuilder, TermPlaceholder } from 'terminology-lib';
import { UtilsService } from 'utils-service-lib';

@Component({
  selector: 'app-add-recipient',
  imports: [CommonModule, EntityPickerComponent, MatIcon, ReactiveFormsModule, MatSelect, MatOption],
  templateUrl: './add-recipient.component.html',
  styleUrl: './add-recipient.component.scss',
})
export class AddRecipientComponent implements OnInit, AfterViewInit {
  formGroup: UntypedFormGroup = new UntypedFormGroup({
    name: new UntypedFormControl('', Validators.required),
    email: new UntypedFormControl(''),
    role: new UntypedFormControl(''),
    editing: new UntypedFormControl(''),
  });

  @Input()
  recipientType: string | undefined;

  @Input()
  recipientRoles?: string[];

  @Input() set emailRequired(emailRequired: boolean) {
    this.setEmailRequired(emailRequired);
  }

  @Output()
  emitAddRecipient: EventEmitter<IRecipientInfo> = new EventEmitter<IRecipientInfo>();

  recipientHeadingLabel?: string;
  recipientNameLabel?: string;
  searchTypes: EntityType[];
  serviceProvider: ISharedServiceProviderService;
  recipientInfoService: IRecipientInfoService;
  renderMode: RenderMode;

  private elementFocus?: ElementRef;

  @ViewChild('focus') set entityInput(elementFocus: ElementRef<HTMLElement>) {
    if (elementFocus) {
      this.elementFocus = elementFocus;
    }
  }

  constructor(
    private sharedServiceProviderServiceFactory: SharedServiceProviderServiceFactory,
    private identityStorage: IdentityStorage,
    @Inject(RUNTIME_ENVIRONMENT) private runtimeEnvironment: IRuntimeEnvironment | undefined
  ) {
    this.renderMode = this.runtimeEnvironment?.renderMode ?? 'web-app';
    this.serviceProvider = this.sharedServiceProviderServiceFactory.getServiceProviderService(this.identityStorage.get()?.namespace);
    this.recipientInfoService = this.serviceProvider.getRecipientInfoService();
    this.searchTypes = this.recipientInfoService.getEntityTypes();
  }

  ngOnInit(): void {
    if (this.recipientType !== 'person') {
      const terminologyService = this.serviceProvider.getTerminologyService();
      const prefix = TerminologySentenceBuilder.format(`${TermPlaceholder.NAMESPACE} ${TermPlaceholder.RECORD_NAME}`, terminologyService);
      this.recipientHeadingLabel = `${prefix} details`;
      this.recipientNameLabel = `${prefix} name`;
    }
  }

  ngAfterViewInit(): void {
    if (this.elementFocus) {
      this.elementFocus.nativeElement.focus();
    }
  }

  addRecipient(): void {
    this.emitAddRecipient.emit(this.formGroup.value);
  }

  onRecipientNameSelected(pickerInfo: IPickerInfo): void {
    this.recipientInfoService.getEntityInfo(pickerInfo.entity).subscribe((recipient) => {
      this.emitAddRecipient.emit({ ...recipient, editing: !recipient.email });
    });
  }

  setEmailRequired(required: boolean) {
    if (required) {
      this.formGroup.get('email')?.setValidators([Validators.required, UtilsService.emailValidator()]);
    } else {
      this.formGroup.get('email')?.clearValidators();
    }
    this.formGroup.updateValueAndValidity();
  }
}
